/**
 * Country reference dataset for the website app.
 *
 * Static ISO 3166-1 reference data (names, codes, telecom dial codes, currency,
 * region, flags) used to drive phone-number inputs, country pickers and contact
 * forms. This is standards data, not business data — it is intentionally local
 * and does not come from the API.
 *
 * Flags are served from flagcdn.com and derived from the ISO alpha-2 code, so
 * swapping the provider is a one-line change in `flagUrl()` below.
 */

/** Flag CDN base — change here to move every flag in the app at once. */
const FLAG_CDN = 'https://flagcdn.com'

/** PNG flag widths flagcdn exposes that we surface on each country. */
const FLAG_SIZES = { small: 'w20', medium: 'w40', large: 'w80', xlarge: 'w160' }

/**
 * Raw table — one tuple per country/territory, alphabetical by name.
 * [ name, iso2, iso3, dialCode, currencyCode, currencySymbol, region, phoneMin, phoneMax ]
 *
 * phoneMin/phoneMax are the national significant number length (digits typed
 * *after* the dial code) and are advisory hints for input length + validation,
 * not a substitute for full libphonenumber parsing.
 */
const RAW_COUNTRIES = [
  ['Afghanistan', 'AF', 'AFG', '+93', 'AFN', '؋', 'Asia', 9, 9],
  ['Åland Islands', 'AX', 'ALA', '+358', 'EUR', '€', 'Europe', 7, 10],
  ['Albania', 'AL', 'ALB', '+355', 'ALL', 'L', 'Europe', 9, 9],
  ['Algeria', 'DZ', 'DZA', '+213', 'DZD', 'د.ج', 'Africa', 9, 9],
  ['American Samoa', 'AS', 'ASM', '+1684', 'USD', '$', 'Oceania', 7, 7],
  ['Andorra', 'AD', 'AND', '+376', 'EUR', '€', 'Europe', 6, 6],
  ['Angola', 'AO', 'AGO', '+244', 'AOA', 'Kz', 'Africa', 9, 9],
  ['Anguilla', 'AI', 'AIA', '+1264', 'XCD', '$', 'Americas', 7, 7],
  ['Antarctica', 'AQ', 'ATA', '+672', null, null, 'Antarctica', 6, 6],
  ['Antigua and Barbuda', 'AG', 'ATG', '+1268', 'XCD', '$', 'Americas', 7, 7],
  ['Argentina', 'AR', 'ARG', '+54', 'ARS', '$', 'Americas', 10, 11],
  ['Armenia', 'AM', 'ARM', '+374', 'AMD', '֏', 'Asia', 8, 8],
  ['Aruba', 'AW', 'ABW', '+297', 'AWG', 'ƒ', 'Americas', 7, 7],
  ['Australia', 'AU', 'AUS', '+61', 'AUD', '$', 'Oceania', 9, 9],
  ['Austria', 'AT', 'AUT', '+43', 'EUR', '€', 'Europe', 7, 13],
  ['Azerbaijan', 'AZ', 'AZE', '+994', 'AZN', '₼', 'Asia', 9, 9],
  ['Bahamas', 'BS', 'BHS', '+1242', 'BSD', '$', 'Americas', 7, 7],
  ['Bahrain', 'BH', 'BHR', '+973', 'BHD', '.د.ب', 'Asia', 8, 8],
  ['Bangladesh', 'BD', 'BGD', '+880', 'BDT', '৳', 'Asia', 10, 10],
  ['Barbados', 'BB', 'BRB', '+1246', 'BBD', '$', 'Americas', 7, 7],
  ['Belarus', 'BY', 'BLR', '+375', 'BYN', 'Br', 'Europe', 9, 9],
  ['Belgium', 'BE', 'BEL', '+32', 'EUR', '€', 'Europe', 9, 9],
  ['Belize', 'BZ', 'BLZ', '+501', 'BZD', '$', 'Americas', 7, 7],
  ['Benin', 'BJ', 'BEN', '+229', 'XOF', 'Fr', 'Africa', 10, 10],
  ['Bermuda', 'BM', 'BMU', '+1441', 'BMD', '$', 'Americas', 7, 7],
  ['Bhutan', 'BT', 'BTN', '+975', 'BTN', 'Nu.', 'Asia', 8, 8],
  ['Bolivia', 'BO', 'BOL', '+591', 'BOB', 'Bs.', 'Americas', 8, 8],
  ['Bonaire, Sint Eustatius and Saba', 'BQ', 'BES', '+599', 'USD', '$', 'Americas', 7, 7],
  ['Bosnia and Herzegovina', 'BA', 'BIH', '+387', 'BAM', 'KM', 'Europe', 8, 8],
  ['Botswana', 'BW', 'BWA', '+267', 'BWP', 'P', 'Africa', 7, 8],
  ['Bouvet Island', 'BV', 'BVT', '+47', 'NOK', 'kr', 'Antarctica', 8, 8],
  ['Brazil', 'BR', 'BRA', '+55', 'BRL', 'R$', 'Americas', 10, 11],
  ['British Indian Ocean Territory', 'IO', 'IOT', '+246', 'USD', '$', 'Asia', 7, 7],
  ['Brunei Darussalam', 'BN', 'BRN', '+673', 'BND', '$', 'Asia', 7, 7],
  ['Bulgaria', 'BG', 'BGR', '+359', 'BGN', 'лв', 'Europe', 8, 9],
  ['Burkina Faso', 'BF', 'BFA', '+226', 'XOF', 'Fr', 'Africa', 8, 8],
  ['Burundi', 'BI', 'BDI', '+257', 'BIF', 'FBu', 'Africa', 8, 8],
  ['Cabo Verde', 'CV', 'CPV', '+238', 'CVE', '$', 'Africa', 7, 7],
  ['Cambodia', 'KH', 'KHM', '+855', 'KHR', '៛', 'Asia', 8, 9],
  ['Cameroon', 'CM', 'CMR', '+237', 'XAF', 'Fr', 'Africa', 9, 9],
  ['Canada', 'CA', 'CAN', '+1', 'CAD', '$', 'Americas', 10, 10],
  ['Cayman Islands', 'KY', 'CYM', '+1345', 'KYD', '$', 'Americas', 7, 7],
  ['Central African Republic', 'CF', 'CAF', '+236', 'XAF', 'Fr', 'Africa', 8, 8],
  ['Chad', 'TD', 'TCD', '+235', 'XAF', 'Fr', 'Africa', 8, 8],
  ['Chile', 'CL', 'CHL', '+56', 'CLP', '$', 'Americas', 9, 9],
  ['China', 'CN', 'CHN', '+86', 'CNY', '¥', 'Asia', 11, 11],
  ['Christmas Island', 'CX', 'CXR', '+61', 'AUD', '$', 'Oceania', 9, 9],
  ['Cocos (Keeling) Islands', 'CC', 'CCK', '+61', 'AUD', '$', 'Oceania', 9, 9],
  ['Colombia', 'CO', 'COL', '+57', 'COP', '$', 'Americas', 10, 10],
  ['Comoros', 'KM', 'COM', '+269', 'KMF', 'Fr', 'Africa', 7, 7],
  ['Congo', 'CG', 'COG', '+242', 'XAF', 'Fr', 'Africa', 9, 9],
  ['Congo, Democratic Republic of the', 'CD', 'COD', '+243', 'CDF', 'FC', 'Africa', 9, 9],
  ['Cook Islands', 'CK', 'COK', '+682', 'NZD', '$', 'Oceania', 5, 5],
  ['Costa Rica', 'CR', 'CRI', '+506', 'CRC', '₡', 'Americas', 8, 8],
  ["Côte d'Ivoire", 'CI', 'CIV', '+225', 'XOF', 'Fr', 'Africa', 10, 10],
  ['Croatia', 'HR', 'HRV', '+385', 'EUR', '€', 'Europe', 8, 9],
  ['Cuba', 'CU', 'CUB', '+53', 'CUP', '$', 'Americas', 8, 8],
  ['Curaçao', 'CW', 'CUW', '+599', 'ANG', 'ƒ', 'Americas', 7, 7],
  ['Cyprus', 'CY', 'CYP', '+357', 'EUR', '€', 'Europe', 8, 8],
  ['Czechia', 'CZ', 'CZE', '+420', 'CZK', 'Kč', 'Europe', 9, 9],
  ['Denmark', 'DK', 'DNK', '+45', 'DKK', 'kr', 'Europe', 8, 8],
  ['Djibouti', 'DJ', 'DJI', '+253', 'DJF', 'Fdj', 'Africa', 8, 8],
  ['Dominica', 'DM', 'DMA', '+1767', 'XCD', '$', 'Americas', 7, 7],
  ['Dominican Republic', 'DO', 'DOM', '+1809', 'DOP', '$', 'Americas', 7, 7],
  ['Ecuador', 'EC', 'ECU', '+593', 'USD', '$', 'Americas', 9, 9],
  ['Egypt', 'EG', 'EGY', '+20', 'EGP', 'E£', 'Africa', 10, 10],
  ['El Salvador', 'SV', 'SLV', '+503', 'USD', '$', 'Americas', 8, 8],
  ['Equatorial Guinea', 'GQ', 'GNQ', '+240', 'XAF', 'Fr', 'Africa', 9, 9],
  ['Eritrea', 'ER', 'ERI', '+291', 'ERN', 'Nfk', 'Africa', 7, 7],
  ['Estonia', 'EE', 'EST', '+372', 'EUR', '€', 'Europe', 7, 8],
  ['Eswatini', 'SZ', 'SWZ', '+268', 'SZL', 'L', 'Africa', 8, 8],
  ['Ethiopia', 'ET', 'ETH', '+251', 'ETB', 'Br', 'Africa', 9, 9],
  ['Falkland Islands', 'FK', 'FLK', '+500', 'FKP', '£', 'Americas', 5, 5],
  ['Faroe Islands', 'FO', 'FRO', '+298', 'DKK', 'kr', 'Europe', 6, 6],
  ['Fiji', 'FJ', 'FJI', '+679', 'FJD', '$', 'Oceania', 7, 7],
  ['Finland', 'FI', 'FIN', '+358', 'EUR', '€', 'Europe', 9, 10],
  ['France', 'FR', 'FRA', '+33', 'EUR', '€', 'Europe', 9, 9],
  ['French Guiana', 'GF', 'GUF', '+594', 'EUR', '€', 'Americas', 9, 9],
  ['French Polynesia', 'PF', 'PYF', '+689', 'XPF', '₣', 'Oceania', 8, 8],
  ['French Southern Territories', 'TF', 'ATF', '+262', 'EUR', '€', 'Antarctica', 9, 9],
  ['Gabon', 'GA', 'GAB', '+241', 'XAF', 'Fr', 'Africa', 8, 8],
  ['Gambia', 'GM', 'GMB', '+220', 'GMD', 'D', 'Africa', 7, 7],
  ['Georgia', 'GE', 'GEO', '+995', 'GEL', '₾', 'Asia', 9, 9],
  ['Germany', 'DE', 'DEU', '+49', 'EUR', '€', 'Europe', 10, 11],
  ['Ghana', 'GH', 'GHA', '+233', 'GHS', '₵', 'Africa', 9, 9],
  ['Gibraltar', 'GI', 'GIB', '+350', 'GIP', '£', 'Europe', 8, 8],
  ['Greece', 'GR', 'GRC', '+30', 'EUR', '€', 'Europe', 10, 10],
  ['Greenland', 'GL', 'GRL', '+299', 'DKK', 'kr', 'Americas', 6, 6],
  ['Grenada', 'GD', 'GRD', '+1473', 'XCD', '$', 'Americas', 7, 7],
  ['Guadeloupe', 'GP', 'GLP', '+590', 'EUR', '€', 'Americas', 9, 9],
  ['Guam', 'GU', 'GUM', '+1671', 'USD', '$', 'Oceania', 7, 7],
  ['Guatemala', 'GT', 'GTM', '+502', 'GTQ', 'Q', 'Americas', 8, 8],
  ['Guernsey', 'GG', 'GGY', '+44', 'GBP', '£', 'Europe', 10, 10],
  ['Guinea', 'GN', 'GIN', '+224', 'GNF', 'Fr', 'Africa', 9, 9],
  ['Guinea-Bissau', 'GW', 'GNB', '+245', 'XOF', 'Fr', 'Africa', 7, 7],
  ['Guyana', 'GY', 'GUY', '+592', 'GYD', '$', 'Americas', 7, 7],
  ['Haiti', 'HT', 'HTI', '+509', 'HTG', 'G', 'Americas', 8, 8],
  ['Heard Island and McDonald Islands', 'HM', 'HMD', '+672', 'AUD', '$', 'Antarctica', 6, 6],
  ['Holy See', 'VA', 'VAT', '+379', 'EUR', '€', 'Europe', 10, 10],
  ['Honduras', 'HN', 'HND', '+504', 'HNL', 'L', 'Americas', 8, 8],
  ['Hong Kong', 'HK', 'HKG', '+852', 'HKD', '$', 'Asia', 8, 8],
  ['Hungary', 'HU', 'HUN', '+36', 'HUF', 'Ft', 'Europe', 9, 9],
  ['Iceland', 'IS', 'ISL', '+354', 'ISK', 'kr', 'Europe', 7, 7],
  ['India', 'IN', 'IND', '+91', 'INR', '₹', 'Asia', 10, 10],
  ['Indonesia', 'ID', 'IDN', '+62', 'IDR', 'Rp', 'Asia', 9, 12],
  ['Iran', 'IR', 'IRN', '+98', 'IRR', '﷼', 'Asia', 10, 10],
  ['Iraq', 'IQ', 'IRQ', '+964', 'IQD', 'ع.د', 'Asia', 10, 10],
  ['Ireland', 'IE', 'IRL', '+353', 'EUR', '€', 'Europe', 9, 9],
  ['Isle of Man', 'IM', 'IMN', '+44', 'GBP', '£', 'Europe', 10, 10],
  ['Israel', 'IL', 'ISR', '+972', 'ILS', '₪', 'Asia', 9, 9],
  ['Italy', 'IT', 'ITA', '+39', 'EUR', '€', 'Europe', 9, 10],
  ['Jamaica', 'JM', 'JAM', '+1876', 'JMD', '$', 'Americas', 7, 7],
  ['Japan', 'JP', 'JPN', '+81', 'JPY', '¥', 'Asia', 10, 10],
  ['Jersey', 'JE', 'JEY', '+44', 'GBP', '£', 'Europe', 10, 10],
  ['Jordan', 'JO', 'JOR', '+962', 'JOD', 'د.ا', 'Asia', 9, 9],
  ['Kazakhstan', 'KZ', 'KAZ', '+7', 'KZT', '₸', 'Asia', 10, 10],
  ['Kenya', 'KE', 'KEN', '+254', 'KES', 'KSh', 'Africa', 9, 9],
  ['Kiribati', 'KI', 'KIR', '+686', 'AUD', '$', 'Oceania', 8, 8],
  ['Korea, Democratic People’s Republic of', 'KP', 'PRK', '+850', 'KPW', '₩', 'Asia', 10, 10],
  ['Korea, Republic of', 'KR', 'KOR', '+82', 'KRW', '₩', 'Asia', 9, 10],
  ['Kosovo', 'XK', 'XKX', '+383', 'EUR', '€', 'Europe', 8, 9],
  ['Kuwait', 'KW', 'KWT', '+965', 'KWD', 'د.ك', 'Asia', 8, 8],
  ['Kyrgyzstan', 'KG', 'KGZ', '+996', 'KGS', 'с', 'Asia', 9, 9],
  ["Lao People's Democratic Republic", 'LA', 'LAO', '+856', 'LAK', '₭', 'Asia', 9, 10],
  ['Latvia', 'LV', 'LVA', '+371', 'EUR', '€', 'Europe', 8, 8],
  ['Lebanon', 'LB', 'LBN', '+961', 'LBP', 'ل.ل', 'Asia', 7, 8],
  ['Lesotho', 'LS', 'LSO', '+266', 'LSL', 'L', 'Africa', 8, 8],
  ['Liberia', 'LR', 'LBR', '+231', 'LRD', '$', 'Africa', 8, 9],
  ['Libya', 'LY', 'LBY', '+218', 'LYD', 'ل.د', 'Africa', 9, 9],
  ['Liechtenstein', 'LI', 'LIE', '+423', 'CHF', 'CHF', 'Europe', 7, 7],
  ['Lithuania', 'LT', 'LTU', '+370', 'EUR', '€', 'Europe', 8, 8],
  ['Luxembourg', 'LU', 'LUX', '+352', 'EUR', '€', 'Europe', 9, 9],
  ['Macao', 'MO', 'MAC', '+853', 'MOP', 'P', 'Asia', 8, 8],
  ['Madagascar', 'MG', 'MDG', '+261', 'MGA', 'Ar', 'Africa', 9, 9],
  ['Malawi', 'MW', 'MWI', '+265', 'MWK', 'MK', 'Africa', 9, 9],
  ['Malaysia', 'MY', 'MYS', '+60', 'MYR', 'RM', 'Asia', 9, 10],
  ['Maldives', 'MV', 'MDV', '+960', 'MVR', 'Rf', 'Asia', 7, 7],
  ['Mali', 'ML', 'MLI', '+223', 'XOF', 'Fr', 'Africa', 8, 8],
  ['Malta', 'MT', 'MLT', '+356', 'EUR', '€', 'Europe', 8, 8],
  ['Marshall Islands', 'MH', 'MHL', '+692', 'USD', '$', 'Oceania', 7, 7],
  ['Martinique', 'MQ', 'MTQ', '+596', 'EUR', '€', 'Americas', 9, 9],
  ['Mauritania', 'MR', 'MRT', '+222', 'MRU', 'UM', 'Africa', 8, 8],
  ['Mauritius', 'MU', 'MUS', '+230', 'MUR', '₨', 'Africa', 8, 8],
  ['Mayotte', 'YT', 'MYT', '+262', 'EUR', '€', 'Africa', 9, 9],
  ['Mexico', 'MX', 'MEX', '+52', 'MXN', '$', 'Americas', 10, 10],
  ['Micronesia', 'FM', 'FSM', '+691', 'USD', '$', 'Oceania', 7, 7],
  ['Moldova', 'MD', 'MDA', '+373', 'MDL', 'L', 'Europe', 8, 8],
  ['Monaco', 'MC', 'MCO', '+377', 'EUR', '€', 'Europe', 8, 9],
  ['Mongolia', 'MN', 'MNG', '+976', 'MNT', '₮', 'Asia', 8, 8],
  ['Montenegro', 'ME', 'MNE', '+382', 'EUR', '€', 'Europe', 8, 8],
  ['Montserrat', 'MS', 'MSR', '+1664', 'XCD', '$', 'Americas', 7, 7],
  ['Morocco', 'MA', 'MAR', '+212', 'MAD', 'د.م.', 'Africa', 9, 9],
  ['Mozambique', 'MZ', 'MOZ', '+258', 'MZN', 'MT', 'Africa', 9, 9],
  ['Myanmar', 'MM', 'MMR', '+95', 'MMK', 'K', 'Asia', 8, 10],
  ['Namibia', 'NA', 'NAM', '+264', 'NAD', '$', 'Africa', 9, 9],
  ['Nauru', 'NR', 'NRU', '+674', 'AUD', '$', 'Oceania', 7, 7],
  ['Nepal', 'NP', 'NPL', '+977', 'NPR', '₨', 'Asia', 10, 10],
  ['Netherlands', 'NL', 'NLD', '+31', 'EUR', '€', 'Europe', 9, 9],
  ['New Caledonia', 'NC', 'NCL', '+687', 'XPF', '₣', 'Oceania', 6, 6],
  ['New Zealand', 'NZ', 'NZL', '+64', 'NZD', '$', 'Oceania', 8, 9],
  ['Nicaragua', 'NI', 'NIC', '+505', 'NIO', 'C$', 'Americas', 8, 8],
  ['Niger', 'NE', 'NER', '+227', 'XOF', 'Fr', 'Africa', 8, 8],
  ['Nigeria', 'NG', 'NGA', '+234', 'NGN', '₦', 'Africa', 10, 10],
  ['Niue', 'NU', 'NIU', '+683', 'NZD', '$', 'Oceania', 4, 4],
  ['Norfolk Island', 'NF', 'NFK', '+672', 'AUD', '$', 'Oceania', 6, 6],
  ['North Macedonia', 'MK', 'MKD', '+389', 'MKD', 'ден', 'Europe', 8, 8],
  ['Northern Mariana Islands', 'MP', 'MNP', '+1670', 'USD', '$', 'Oceania', 7, 7],
  ['Norway', 'NO', 'NOR', '+47', 'NOK', 'kr', 'Europe', 8, 8],
  ['Oman', 'OM', 'OMN', '+968', 'OMR', 'ر.ع.', 'Asia', 8, 8],
  ['Pakistan', 'PK', 'PAK', '+92', 'PKR', '₨', 'Asia', 10, 10],
  ['Palau', 'PW', 'PLW', '+680', 'USD', '$', 'Oceania', 7, 7],
  ['Palestine, State of', 'PS', 'PSE', '+970', 'ILS', '₪', 'Asia', 9, 9],
  ['Panama', 'PA', 'PAN', '+507', 'PAB', 'B/.', 'Americas', 8, 8],
  ['Papua New Guinea', 'PG', 'PNG', '+675', 'PGK', 'K', 'Oceania', 8, 8],
  ['Paraguay', 'PY', 'PRY', '+595', 'PYG', '₲', 'Americas', 9, 9],
  ['Peru', 'PE', 'PER', '+51', 'PEN', 'S/', 'Americas', 9, 9],
  ['Philippines', 'PH', 'PHL', '+63', 'PHP', '₱', 'Asia', 10, 10],
  ['Pitcairn', 'PN', 'PCN', '+64', 'NZD', '$', 'Oceania', 9, 9],
  ['Poland', 'PL', 'POL', '+48', 'PLN', 'zł', 'Europe', 9, 9],
  ['Portugal', 'PT', 'PRT', '+351', 'EUR', '€', 'Europe', 9, 9],
  ['Puerto Rico', 'PR', 'PRI', '+1787', 'USD', '$', 'Americas', 7, 7],
  ['Qatar', 'QA', 'QAT', '+974', 'QAR', 'ر.ق', 'Asia', 8, 8],
  ['Réunion', 'RE', 'REU', '+262', 'EUR', '€', 'Africa', 9, 9],
  ['Romania', 'RO', 'ROU', '+40', 'RON', 'lei', 'Europe', 9, 9],
  ['Russian Federation', 'RU', 'RUS', '+7', 'RUB', '₽', 'Europe', 10, 10],
  ['Rwanda', 'RW', 'RWA', '+250', 'RWF', 'FRw', 'Africa', 9, 9],
  ['Saint Barthélemy', 'BL', 'BLM', '+590', 'EUR', '€', 'Americas', 9, 9],
  ['Saint Helena, Ascension and Tristan da Cunha', 'SH', 'SHN', '+290', 'SHP', '£', 'Africa', 4, 5],
  ['Saint Kitts and Nevis', 'KN', 'KNA', '+1869', 'XCD', '$', 'Americas', 7, 7],
  ['Saint Lucia', 'LC', 'LCA', '+1758', 'XCD', '$', 'Americas', 7, 7],
  ['Saint Martin (French part)', 'MF', 'MAF', '+590', 'EUR', '€', 'Americas', 9, 9],
  ['Saint Pierre and Miquelon', 'PM', 'SPM', '+508', 'EUR', '€', 'Americas', 6, 6],
  ['Saint Vincent and the Grenadines', 'VC', 'VCT', '+1784', 'XCD', '$', 'Americas', 7, 7],
  ['Samoa', 'WS', 'WSM', '+685', 'WST', 'T', 'Oceania', 7, 7],
  ['San Marino', 'SM', 'SMR', '+378', 'EUR', '€', 'Europe', 10, 10],
  ['Sao Tome and Principe', 'ST', 'STP', '+239', 'STN', 'Db', 'Africa', 7, 7],
  ['Saudi Arabia', 'SA', 'SAU', '+966', 'SAR', 'ر.س', 'Asia', 9, 9],
  ['Senegal', 'SN', 'SEN', '+221', 'XOF', 'Fr', 'Africa', 9, 9],
  ['Serbia', 'RS', 'SRB', '+381', 'RSD', 'дин', 'Europe', 8, 9],
  ['Seychelles', 'SC', 'SYC', '+248', 'SCR', '₨', 'Africa', 7, 7],
  ['Sierra Leone', 'SL', 'SLE', '+232', 'SLE', 'Le', 'Africa', 8, 8],
  ['Singapore', 'SG', 'SGP', '+65', 'SGD', '$', 'Asia', 8, 8],
  ['Sint Maarten (Dutch part)', 'SX', 'SXM', '+1721', 'ANG', 'ƒ', 'Americas', 7, 7],
  ['Slovakia', 'SK', 'SVK', '+421', 'EUR', '€', 'Europe', 9, 9],
  ['Slovenia', 'SI', 'SVN', '+386', 'EUR', '€', 'Europe', 8, 8],
  ['Solomon Islands', 'SB', 'SLB', '+677', 'SBD', '$', 'Oceania', 7, 7],
  ['Somalia', 'SO', 'SOM', '+252', 'SOS', 'Sh', 'Africa', 7, 9],
  ['South Africa', 'ZA', 'ZAF', '+27', 'ZAR', 'R', 'Africa', 9, 9],
  ['South Georgia and the South Sandwich Islands', 'GS', 'SGS', '+500', 'GBP', '£', 'Antarctica', 5, 5],
  ['South Sudan', 'SS', 'SSD', '+211', 'SSP', '£', 'Africa', 9, 9],
  ['Spain', 'ES', 'ESP', '+34', 'EUR', '€', 'Europe', 9, 9],
  ['Sri Lanka', 'LK', 'LKA', '+94', 'LKR', '₨', 'Asia', 9, 9],
  ['Sudan', 'SD', 'SDN', '+249', 'SDG', 'ج.س', 'Africa', 9, 9],
  ['Suriname', 'SR', 'SUR', '+597', 'SRD', '$', 'Americas', 7, 7],
  ['Svalbard and Jan Mayen', 'SJ', 'SJM', '+47', 'NOK', 'kr', 'Europe', 8, 8],
  ['Sweden', 'SE', 'SWE', '+46', 'SEK', 'kr', 'Europe', 7, 9],
  ['Switzerland', 'CH', 'CHE', '+41', 'CHF', 'CHF', 'Europe', 9, 9],
  ['Syrian Arab Republic', 'SY', 'SYR', '+963', 'SYP', '£', 'Asia', 9, 9],
  ['Taiwan', 'TW', 'TWN', '+886', 'TWD', 'NT$', 'Asia', 9, 9],
  ['Tajikistan', 'TJ', 'TJK', '+992', 'TJS', 'ЅМ', 'Asia', 9, 9],
  ['Tanzania', 'TZ', 'TZA', '+255', 'TZS', 'TSh', 'Africa', 9, 9],
  ['Thailand', 'TH', 'THA', '+66', 'THB', '฿', 'Asia', 9, 9],
  ['Timor-Leste', 'TL', 'TLS', '+670', 'USD', '$', 'Asia', 7, 8],
  ['Togo', 'TG', 'TGO', '+228', 'XOF', 'Fr', 'Africa', 8, 8],
  ['Tokelau', 'TK', 'TKL', '+690', 'NZD', '$', 'Oceania', 4, 4],
  ['Tonga', 'TO', 'TON', '+676', 'TOP', 'T$', 'Oceania', 5, 5],
  ['Trinidad and Tobago', 'TT', 'TTO', '+1868', 'TTD', '$', 'Americas', 7, 7],
  ['Tunisia', 'TN', 'TUN', '+216', 'TND', 'د.ت', 'Africa', 8, 8],
  ['Türkiye', 'TR', 'TUR', '+90', 'TRY', '₺', 'Asia', 10, 10],
  ['Turkmenistan', 'TM', 'TKM', '+993', 'TMT', 'm', 'Asia', 8, 8],
  ['Turks and Caicos Islands', 'TC', 'TCA', '+1649', 'USD', '$', 'Americas', 7, 7],
  ['Tuvalu', 'TV', 'TUV', '+688', 'AUD', '$', 'Oceania', 5, 5],
  ['Uganda', 'UG', 'UGA', '+256', 'UGX', 'USh', 'Africa', 9, 9],
  ['Ukraine', 'UA', 'UKR', '+380', 'UAH', '₴', 'Europe', 9, 9],
  ['United Arab Emirates', 'AE', 'ARE', '+971', 'AED', 'د.إ', 'Asia', 9, 9],
  ['United Kingdom', 'GB', 'GBR', '+44', 'GBP', '£', 'Europe', 10, 10],
  ['United States', 'US', 'USA', '+1', 'USD', '$', 'Americas', 10, 10],
  ['United States Minor Outlying Islands', 'UM', 'UMI', '+1', 'USD', '$', 'Oceania', 10, 10],
  ['Uruguay', 'UY', 'URY', '+598', 'UYU', '$U', 'Americas', 8, 8],
  ['Uzbekistan', 'UZ', 'UZB', '+998', 'UZS', "so'm", 'Asia', 9, 9],
  ['Vanuatu', 'VU', 'VUT', '+678', 'VUV', 'Vt', 'Oceania', 7, 7],
  ['Venezuela', 'VE', 'VEN', '+58', 'VES', 'Bs.', 'Americas', 10, 10],
  ['Viet Nam', 'VN', 'VNM', '+84', 'VND', '₫', 'Asia', 9, 9],
  ['Virgin Islands (British)', 'VG', 'VGB', '+1284', 'USD', '$', 'Americas', 7, 7],
  ['Virgin Islands (U.S.)', 'VI', 'VIR', '+1340', 'USD', '$', 'Americas', 7, 7],
  ['Wallis and Futuna', 'WF', 'WLF', '+681', 'XPF', '₣', 'Oceania', 6, 6],
  ['Western Sahara', 'EH', 'ESH', '+212', 'MAD', 'د.م.', 'Africa', 9, 9],
  ['Yemen', 'YE', 'YEM', '+967', 'YER', '﷼', 'Asia', 9, 9],
  ['Zambia', 'ZM', 'ZMB', '+260', 'ZMW', 'ZK', 'Africa', 9, 9],
  ['Zimbabwe', 'ZW', 'ZWE', '+263', 'ZWG', 'ZiG', 'Africa', 9, 9],
]

/** Regional-indicator flag emoji for an ISO alpha-2 code — 'IN' → 🇮🇳. */
export function flagEmoji(iso2) {
  const code = String(iso2 || '').toUpperCase()
  if (!/^[A-Z]{2}$/.test(code)) return ''
  return String.fromCodePoint(...[...code].map((ch) => 0x1f1a5 + ch.charCodeAt(0)))
}

/**
 * Flag image URL for an ISO alpha-2 code.
 * @param {string} iso2 - e.g. 'IN'
 * @param {'small'|'medium'|'large'|'xlarge'|'svg'} [size='medium']
 */
export function flagUrl(iso2, size = 'medium') {
  const code = String(iso2 || '').toLowerCase()
  if (!code) return ''
  if (size === 'svg') return `${FLAG_CDN}/${code}.svg`
  return `${FLAG_CDN}/${FLAG_SIZES[size] || FLAG_SIZES.medium}/${code}.png`
}

/**
 * @typedef {Object} Country
 * @property {string} name              Common English name — 'India'
 * @property {string} iso2              ISO 3166-1 alpha-2 — 'IN'
 * @property {string} iso3              ISO 3166-1 alpha-3 — 'IND'
 * @property {string} dialCode          Telecom country calling code — '+91'
 * @property {number} dialCodeDigits    Same code without '+' — 91
 * @property {{code: string|null, symbol: string|null}} currency
 * @property {string} region            Continent bucket — 'Asia'
 * @property {{min: number, max: number}} phoneLength  National number digits
 * @property {string} emoji             Flag emoji — 🇮🇳
 * @property {string} flag              Default flag image (40px PNG)
 * @property {{small: string, medium: string, large: string, xlarge: string, svg: string}} flags
 * @property {string} label             'India (+91)' — ready for pickers
 */

/** @type {Country[]} Every country/territory, sorted by name. */
export const COUNTRIES = RAW_COUNTRIES.map(
  ([name, iso2, iso3, dialCode, currencyCode, currencySymbol, region, phoneMin, phoneMax]) => ({
    name,
    iso2,
    iso3,
    dialCode,
    dialCodeDigits: Number(dialCode.replace('+', '')),
    currency: { code: currencyCode, symbol: currencySymbol },
    region,
    phoneLength: { min: phoneMin, max: phoneMax },
    emoji: flagEmoji(iso2),
    flag: flagUrl(iso2, 'medium'),
    flags: {
      small: flagUrl(iso2, 'small'),
      medium: flagUrl(iso2, 'medium'),
      large: flagUrl(iso2, 'large'),
      xlarge: flagUrl(iso2, 'xlarge'),
      svg: flagUrl(iso2, 'svg'),
    },
    label: `${name} (${dialCode})`,
  }),
).sort((a, b) => a.name.localeCompare(b.name))

/** Lookup maps — build once, read everywhere. */
export const COUNTRY_BY_ISO2 = Object.fromEntries(COUNTRIES.map((c) => [c.iso2, c]))
export const COUNTRY_BY_ISO3 = Object.fromEntries(COUNTRIES.map((c) => [c.iso3, c]))

/**
 * Sovereign/primary country for the dial codes several territories share, so
 * '+44' resolves to the United Kingdom rather than Guernsey.
 */
const PRIMARY_DIAL_CODE_ISO2 = {
  '+1': 'US',
  '+7': 'RU',
  '+44': 'GB',
  '+47': 'NO',
  '+61': 'AU',
  '+64': 'NZ',
  '+212': 'MA',
  '+262': 'RE',
  '+358': 'FI',
  '+500': 'FK',
  '+590': 'GP',
  '+599': 'CW',
  '+672': 'NF',
}

/** Dial code → countries sharing it, primary first (+1 → US, CA, UM). */
export const COUNTRIES_BY_DIAL_CODE = COUNTRIES.reduce((acc, country) => {
  ;(acc[country.dialCode] ||= []).push(country)
  return acc
}, {})

for (const [dialCode, iso2] of Object.entries(PRIMARY_DIAL_CODE_ISO2)) {
  const bucket = COUNTRIES_BY_DIAL_CODE[dialCode]
  const index = bucket?.findIndex((c) => c.iso2 === iso2) ?? -1
  if (index > 0) bucket.unshift(...bucket.splice(index, 1))
}

/** Countries our audience uses most — pin these to the top of pickers. */
export const POPULAR_COUNTRY_ISO2 = ['IN', 'US', 'GB', 'AE', 'AU', 'CA', 'SG', 'NZ', 'MY', 'NP']
export const POPULAR_COUNTRIES = POPULAR_COUNTRY_ISO2.map((iso2) => COUNTRY_BY_ISO2[iso2])

export const DEFAULT_COUNTRY_ISO2 = 'IN'
export const DEFAULT_COUNTRY = COUNTRY_BY_ISO2[DEFAULT_COUNTRY_ISO2]

/** Continent buckets present in the dataset, alphabetical. */
export const COUNTRY_REGIONS = [...new Set(COUNTRIES.map((c) => c.region))].sort()

/** @returns {Country|undefined} */
export function getCountryByIso2(iso2) {
  return COUNTRY_BY_ISO2[String(iso2 || '').toUpperCase()]
}

/** @returns {Country|undefined} */
export function getCountryByIso3(iso3) {
  return COUNTRY_BY_ISO3[String(iso3 || '').toUpperCase()]
}

/**
 * First country for a dial code — '+1' and '1' both resolve.
 * Shared codes return the primary entry (alphabetically first).
 * @returns {Country|undefined}
 */
export function getCountryByDialCode(dialCode) {
  const normalized = `+${String(dialCode || '').replace(/\D/g, '')}`
  return COUNTRIES_BY_DIAL_CODE[normalized]?.[0]
}

/**
 * Resolve the country from a full international number by longest-prefix match,
 * so '+1671…' picks Guam over the United States.
 * @param {string} phone - e.g. '+91 98765 43210'
 * @returns {Country|undefined}
 */
export function getCountryByPhone(phone) {
  const digits = String(phone || '').replace(/\D/g, '')
  if (!digits) return undefined
  for (let len = 4; len >= 1; len -= 1) {
    const match = COUNTRIES_BY_DIAL_CODE[`+${digits.slice(0, len)}`]
    if (match) return match[0]
  }
  return undefined
}

/**
 * Does a country match a type-ahead query? Matches on name, ISO codes and
 * dial code, so '971', 'ae' and 'emirates' all find the UAE.
 */
export function matchesCountryQuery(country, query) {
  const term = String(query || '').trim().toLowerCase()
  if (!term) return true
  if (!country) return false
  const digits = term.replace(/\D/g, '')
  return (
    country.name.toLowerCase().includes(term) ||
    country.iso2.toLowerCase() === term ||
    country.iso3.toLowerCase() === term ||
    (Boolean(digits) && country.dialCode.replace('+', '').startsWith(digits))
  )
}

/**
 * Name / code / dial-code search for a picker's type-ahead.
 * @returns {Country[]}
 */
export function searchCountries(query) {
  return COUNTRIES.filter((country) => matchesCountryQuery(country, query))
}

/** True when `number`'s digit count fits the country's national number length. */
export function isValidNationalNumber(iso2, number) {
  const country = getCountryByIso2(iso2)
  if (!country) return false
  const digits = String(number || '').replace(/\D/g, '').length
  return digits >= country.phoneLength.min && digits <= country.phoneLength.max
}

/** '+919876543210' from ('IN', '98765 43210'). */
export function toE164(iso2, number) {
  const country = getCountryByIso2(iso2)
  const digits = String(number || '').replace(/\D/g, '')
  if (!country || !digits) return ''
  return `${country.dialCode}${digits}`
}

/**
 * react-select options (value = ISO alpha-2), popular countries pinned first.
 * Each option carries the country so a custom Option/SingleValue can render the flag.
 * @type {{value: string, label: string, dialCode: string, flag: string, emoji: string, country: Country}[]}
 */
export const COUNTRY_SELECT_OPTIONS = [
  ...POPULAR_COUNTRIES,
  ...COUNTRIES.filter((c) => !POPULAR_COUNTRY_ISO2.includes(c.iso2)),
].map((country) => ({
  value: country.iso2,
  label: country.name,
  dialCode: country.dialCode,
  flag: country.flag,
  emoji: country.emoji,
  country,
}))

/** react-select options for a phone-prefix picker — '🇮🇳 +91'. */
export const DIAL_CODE_SELECT_OPTIONS = COUNTRY_SELECT_OPTIONS.map((option) => ({
  ...option,
  label: `${option.emoji} ${option.dialCode}`,
}))

export default COUNTRIES
